// CreateSettlementsDeposit.tsx
import React, {useEffect} from 'react';
import { createPortal } from 'react-dom';
import { useForm, FormProvider, SubmitHandler, Controller } from 'react-hook-form';
import styles from './CreateSettlementsDeposit.module.css';
import { Loading } from '../../../../../../components/Loading/Loading';
import { getRequestErrorMessage, pushNotify } from '../../../../../../components/Notifications';
import { sanitizeCurrencyAmount } from '../../../../../../utils/functions/currencyAmount';
import { ControlledInput } from '../../../../../../components/ControlledInput/ControlledInput';
import SelectType, { TItem } from '../CustomFilterModal/SelectType';
import {
  useGetPlatformWalletAddressesQuery,
  useCreateSettlementMutation
} from '../../../../../../redux/services/SettlementsApi';
import { useGetSelfQuery } from '../../../../../../redux/services/MerchantApi';

interface FormValues {
  currency: string;
  wallet: string;
  amount: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateSettlementsDeposit: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const {
    data: wallets = [],
    isLoading: walletsLoading
  } = useGetPlatformWalletAddressesQuery(undefined, { skip: !open, refetchOnMountOrArgChange: true });
  const {
    data: self,
    isLoading: selfLoading
  } = useGetSelfQuery(undefined, { skip: !open, refetchOnMountOrArgChange: true });

  const [createSettlement, { isLoading: isCreating }] = useCreateSettlementMutation();
  const maxDeposit = self?.balance_merchant_check ?? 0;

  const currencyItems: TItem<string>[] = [{ id: 'USDT-TRC20', name: 'USDT-TRC20' }];
  const walletItems: TItem<string>[] = wallets.map(w => ({ id: w, name: w }));

  const methods = useForm<FormValues>({
    defaultValues: { currency: '', wallet: '', amount: '' },
    mode: 'onChange'
  });
  const {
    handleSubmit,
    formState: { isValid, errors },
    control,
     reset,
  } = methods;

  useEffect(() => {
    if (currencyItems.length && walletItems.length) {
      reset({
        currency: currencyItems[0].id,
        wallet:   walletItems[0].id,
        amount:   ''
      });
    }
  }, [currencyItems.length, walletItems.length, reset]);


  const onSubmit: SubmitHandler<FormValues> = async ({ currency, wallet, amount }) => {
    const num = Number(sanitizeCurrencyAmount(amount) || '');
    try {
      await createSettlement({
        direction: 'MERCHANT_TO_PLATFORM',
        merchant_wallet_address: wallet,
        currency,
        amount: num
      }).unwrap();
      pushNotify('Заявка на пополнение создана', 'Success');
      onSuccess?.();
      onClose();
    } catch (error) {
      pushNotify(getRequestErrorMessage(error, 'Не удалось создать заявку на пополнение'), 'Ошибка');
    }
  };

  if (!open) return null;
  if (walletsLoading || selfLoading || isCreating) {
    return createPortal(<Loading />, document.body);
  }

  return createPortal(
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="create-settlement-deposit-title" onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h2 id="create-settlement-deposit-title" className={styles.title}>Создать пополнение</h2>
          <button type="button" aria-label="Закрыть" className={styles.closeBtn} onClick={onClose}>&times;</button>
        </div>
        <p className={styles.subtitle}>Введите данные для создания пополнения.</p>

        <FormProvider {...methods}>
          <form id="deposit-form" className={styles.form} onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.field}>
              <Controller
                name="currency"
                control={control}
                render={({ field }) => (
                  <SelectType
                    title="Валюта"
                    items={currencyItems}
                    value={field.value}
                    onChange={field.onChange}
                    error={Boolean(errors.currency)}
                  />
                )}
              />
            </div>

            <div className={styles.field}>
              <Controller
                name="wallet"
                control={control}
                render={({ field }) => (
                  <SelectType
                    title="Адрес площадки"
                    items={walletItems}
                    value={field.value}
                    onChange={field.onChange}
                    error={Boolean(errors.wallet)}
                  />
                )}
              />
            </div>

            <div className={styles.field}>
              <label className={styles.label}>Сумма</label>
              <ControlledInput
                name="amount"
                placeholder="Введите сумму"
                rules={{
                  required: 'Обязательно',
                  validate: v => {
                    const val = Number(sanitizeCurrencyAmount(v) || '');
                    if (!Number.isFinite(val) || val <= 0) return 'Должна быть больше 0';
                    if (val > maxDeposit) return `Максимальная сумма не может превышать ${maxDeposit.toFixed(2)}`;
                    return true;
                  }
                }}
                errorText={errors.amount?.message as string}
                type="text"
                blockStyle={{ width: '100%' }}
              />
              {errors.amount && <span className={styles.errorText}>{errors.amount.message}</span>}
            </div>

            <div className={styles.actions}>
              <button
                type="submit"
                form="deposit-form"
                className={styles.button}
                disabled={!isValid || isCreating}
              >
                Создать
              </button>
            </div>
          </form>
        </FormProvider>
      </div>
    </>,
    document.body
  );
};
